import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { TableListComp, EmptyCollection, DeletePopUp, PageLoader, STableLoader } from '../../../../shared_elements';
import { PlannerSideNav, IconKpiRow, pageSx, useSnackbar } from '../../shared_components';
import '../../shared_components/assets/styles/plannerTracker.scss';
import { PlannerToolbar, PlanDetailDialog, PlannerCRU, ConfirmActionDialog, PlannerList, ChangePlanStage } from '../components';
import { plannerHd, plannerFilterOps, ROLES } from '../constants';
import { buildListQuery, buildKpiCards } from '../utils';
import { checkApiStatus, downloadFileType } from '../../../../utils_v2';
import { globalGetService, globalPostService, globalDeleteService, globalExportService } from '../../../../utils_v2/globalApiServices';
import {
  getProgramPlannerListApi, getProgramPlanDetailApi, deleteProgramPlanApi,
  markPlanReadyApi, launchProgramPlanApi, cancelProgramPlanApi, closeProgramPlanApi,
  getStageTemplatesApi, getLessorsListApi, getLesseesListApi, getInspectionTypesApi,
  exportProgramPlannerListApi,
} from '../apiService';
import { trackActivity } from '../../../../utils/mixpanel';

const errorMessage = (response) => (response && response.data && response.data.message) || 'Unable to process the request due to a technical error.';

const ProgramPlanner = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [plans, setPlans] = useState([]);
  const [pagination, setPagination] = useState({});
  const [kpis, setKpis] = useState({});
  const [tabCounts, setTabCounts] = useState({});
  const [options, setOptions] = useState({ lessors: [], lessees: [], inspectionTypes: [], stageTemplates: [] });
  const [role, setRole] = useState('Project Lead');
  const [tab, setTab] = useState('all');
  const [filter, setFilter] = useState({});
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [viewPlan, setViewPlan] = useState(null);
  const [plannerCRU, setPlannerCRU] = useState({ modal: false, data: null });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [stageChangeTarget, setStageChangeTarget] = useState(null);

  const permissions = ROLES[role];

  const filterMenu = {
    ...plannerFilterOps,
    lessor: { ...plannerFilterOps.lessor, labelKey: 'name', valueKey: 'id', options: options?.lessors },
    itype: { ...plannerFilterOps.itype, labelKey: 'name', valueKey: 'id', options: options?.inspectionTypes },
  };

  const getOptionsData = () => {
    globalGetService(getLessorsListApi()).then((response) => {
      if (checkApiStatus(response)) {
        const data = response?.data?.data;
        setOptions((prev) => ({ ...prev, lessors: data?.lessors || [] }));
      }
    });
    globalGetService(getLesseesListApi()).then((response) => {
      if (checkApiStatus(response)) {
        const data = response?.data?.data;
        setOptions((prev) => ({ ...prev, lessees: data?.lessees || [] }));
      }
    });
    // globalGetService(getInspectionTypesApi()).then((response) => {
    //   if (checkApiStatus(response)) {
    //     setOptions((prev) => ({ ...prev, inspectionTypes: response?.data?.data || [] }));
    //   }
    // });
    globalGetService(getStageTemplatesApi()).then((response) => {
      if (checkApiStatus(response)) {
        setOptions((prev) => ({ ...prev, stageTemplates: response?.data?.data || [] }));
      }
    });
  };

  useEffect(() => {
    getOptionsData();
    fetchPlans();
    trackActivity('Project Management', {
      event_type: 'Page Visited',
      page_title: 'Planner Tracker Program Planner',
    });
  }, []);

  const fetchPlans = (overrides = {}) => {
    const params = { tab, filter, sort, page, rowsPerPage, ...overrides };
    setLoading(true);
    globalGetService(getProgramPlannerListApi(), buildListQuery(params)).then((response) => {
      setLoading(false);
      setInitialLoading(false);
      if (checkApiStatus(response)) {
        const data = response?.data?.data;
        setPlans(data?.list || []);
        setPagination(data?.pagination || {});
        setKpis(data?.kpis || {});
        setTabCounts(data?.tab_counts || {});
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
      }
    });
  };

  const handleTabChange = (value) => {
    setTab(value);
    setPage(0);
    fetchPlans({ tab: value, page: 0 });
  };

  const handleFilterApply = (query) => {
    setFilter(query);
    setPage(0);
    fetchPlans({ filter: query, page: 0 });
  };

  const createSortHandler = (key) => {
    const nextSort = { key, dir: sort.key === key ? -sort.dir : 1 };
    setSort(nextSort);
    setPage(0);
    fetchPlans({ sort: nextSort, page: 0 });
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchPlans({ page: newPage });
  };

  const handleRowsPerPageChange = (event) => {
    const value = event.target.value;
    setRowsPerPage(value);
    setPage(0);
    fetchPlans({ rowsPerPage: value, page: 0 });
  };

  const handleChangeStatus = (plan, nextStatus) => {
    if (!permissions.edit) return;
    if (nextStatus === 'Ready to Launch') {
      setActionLoading(true);
      globalPostService(markPlanReadyApi(plan?.slug), {}).then((response) => {
        setActionLoading(false);
        if (checkApiStatus(response)) {
          enqueueSnackbar('Status → Ready to Launch', { variant: 'success' });
          fetchPlans();
          trackActivity('Project Management', {
            event_type: 'Mark Plan Ready Success',
            page_title: 'Planner Tracker Program Planner',
            plan_slug: plan?.slug || '',
            response_msg: response?.data?.message,
          });
        } else {
          enqueueSnackbar(errorMessage(response), { variant: 'error' });
          trackActivity('Project Management', {
            event_type: 'Mark Plan Ready Failed',
            page_title: 'Planner Tracker Program Planner',
            plan_slug: plan?.slug || '',
            error_source: 'Backend',
            error_message: errorMessage(response),
          });
        }
      });
      return;
    }
    if (nextStatus === 'Launched') {
      if (!permissions.launch) {
        enqueueSnackbar('Your role cannot launch projects', { variant: 'error' });
        return;
      }
      setConfirmAction({ type: 'launch', plan });
      return;
    }
    if (nextStatus === 'Cancelled') {
      setConfirmAction({ type: 'cancel', plan });
      return;
    }
    if (nextStatus === 'Closed') {
      setConfirmAction({ type: 'close', plan });
      return;
    }
    enqueueSnackbar(`${plan?.status?.label || 'Current Status'} → ${nextStatus} isn't a supported direct transition`, { variant: 'error' });
  };

  const handleConfirmAction = () => {
    const { type, plan } = confirmAction;
    const url = type === 'launch' ? launchProgramPlanApi(plan?.slug)
      : type === 'close' ? closeProgramPlanApi(plan?.slug)
        : cancelProgramPlanApi(plan?.slug);
    const data = type === 'launch' || type === 'close' ? {} : { reason: 'other', notes: '' };
    const actionLabel = type === 'launch' ? 'Launch Plan' : type === 'close' ? 'Close Plan' : 'Cancel Plan';

    setActionLoading(true);
    globalPostService(url, data).then((response) => {
      setActionLoading(false);
      if (checkApiStatus(response)) {
        enqueueSnackbar(
          type === 'launch' ? 'Project launched — data migrated, fields locked'
            : type === 'close' ? 'Project closed'
              : 'Project cancelled & archived',
          { variant: type === 'cancel' ? 'info' : 'success' },
        );
        setViewPlan(null);
        setConfirmAction(null);
        fetchPlans();
        trackActivity('Project Management', {
          event_type: `${actionLabel} Success`,
          page_title: 'Planner Tracker Program Planner',
          plan_slug: plan?.slug || '',
          request_body: data,
          response_msg: response?.data?.message,
        });
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
        setConfirmAction(null);
        trackActivity('Project Management', {
          event_type: `${actionLabel} Failed`,
          page_title: 'Planner Tracker Program Planner',
          plan_slug: plan?.slug || '',
          request_body: data,
          error_source: 'Backend',
          error_message: errorMessage(response),
        });
      }
    });
  };

  const handleExport = () => {
    setExporting(true);
    enqueueSnackbar('Plans report downloading…', { variant: 'info' });
    const { per_page, page: pageParam, ...query } = buildListQuery({ tab, filter, sort, page, rowsPerPage });
    globalExportService(exportProgramPlannerListApi(), { ...query, download: 'xlsx' }).then((fileData) => {
      setExporting(false);
      downloadFileType(fileData, `ProgramPlannerPlans_${moment().format('YYYY-MM-DD')}`, '.xlsx');
      trackActivity('Project Management', {
        event_type: 'Export Program Plans Success',
        page_title: 'Planner Tracker Program Planner',
        file_extension: 'xlsx',
        file_name: `ProgramPlannerPlans_${moment().format('YYYY-MM-DD')}`,
        filter: query,
      });
    }).catch(() => {
      setExporting(false);
      enqueueSnackbar('Unable to download the plans report.', { variant: 'error' });
      trackActivity('Project Management', {
        event_type: 'Export Program Plans Failed',
        page_title: 'Planner Tracker Program Planner',
        file_extension: 'xlsx',
        filter: query,
        error_source: 'Backend',
      });
    });
  };


  const handleEditPlan = (target) => {
    setActionLoading(true);
    globalGetService(getProgramPlanDetailApi(target?.slug)).then((response) => {
      setActionLoading(false);
      if (checkApiStatus(response)) {
        setPlannerCRU({ modal: true, data: response?.data?.data });
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
      }
    });
  };

  const handleConfirmDelete = () => {
    setDeleting(true);
    globalDeleteService(deleteProgramPlanApi(deleteTarget?.slug)).then((response) => {
      setDeleting(false);
      if (checkApiStatus(response)) {
        enqueueSnackbar('Plan deleted', { variant: 'success' });
        setDeleteTarget(null);
        fetchPlans();
        trackActivity('Project Management', {
          event_type: 'Delete Program Plan Success',
          page_title: 'Planner Tracker Program Planner',
          plan_slug: deleteTarget?.slug || '',
          response_msg: response?.data?.message,
        });
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
        setDeleteTarget(null);
        trackActivity('Project Management', {
          event_type: 'Delete Program Plan Failed',
          page_title: 'Planner Tracker Program Planner',
          plan_slug: deleteTarget?.slug || '',
          error_source: 'Backend',
          error_message: errorMessage(response),
        });
      }
    });
  };

  return (
    <div style={{ display: 'flex', marginLeft: 56, marginTop: 15 }}>
      <PlannerSideNav activeKey="planner" />
      <section className="projects-management-apps" style={{ ...pageSx, flex: 1, minWidth: 0 }}>

          <IconKpiRow cards={buildKpiCards(kpis)} onSelect={handleTabChange} />
          <PlannerToolbar
            tab={tab}
            onChangeTab={handleTabChange}
            filter={filter}
            filterMenu={filterMenu}
            onApplyFilter={handleFilterApply}
            role={role}
            onChangeRole={setRole}
            canCreate={permissions.create}
            onAddPlan={() => setPlannerCRU({ modal: true, data: null })}
            onExport={handleExport}
            exporting={exporting}
            resultCount={pagination?.total || plans?.length}
            tabCounts={tabCounts}
          />

          <div style={{ marginTop: '5px' }} />

          {initialLoading ? <STableLoader count={plannerHd.length} rowsFill={8} /> : (
            <TableListComp
              panelClassName="pt-scroll-panel pt-scroll-panel--planner-table"
              heads={plannerHd}
              sort={sort.key || ''}
              sort_by={sort.dir === 1 ? 'asc' : 'desc'}
              createSortHandler={createSortHandler}
              data={(plans || []).map((plan) => (
                <PlannerList
                  key={plan?.id}
                  plan={plan}
                  canEdit={permissions.edit}
                  canDelete={permissions.del}
                  canChangeStatus={permissions.edit}
                  canEditStage={permissions.stages}
                  onView={setViewPlan}
                  onEdit={handleEditPlan}
                  onDelete={setDeleteTarget}
                  onChangeStatus={handleChangeStatus}
                  onEditStage={setStageChangeTarget}
                />
              ))}
              noRecord={!loading && !plans?.length ? <EmptyCollection title="No records found" /> : null}
              pagination={pagination}
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handleRowsPerPageChange}
            />
          )}

        {viewPlan ? (
          <PlanDetailDialog
            plan={viewPlan}
            canLaunch={permissions.launch}
            canConfigureStages={permissions.stages}
            onClose={() => setViewPlan(null)}
            onLaunch={() => setConfirmAction({ type: 'launch', plan: viewPlan })}
            onCancelPlan={() => setConfirmAction({ type: 'cancel', plan: viewPlan })}
            onEdit={() => { handleEditPlan(viewPlan); setViewPlan(null); }}
            onConfigureStages={() => enqueueSnackbar('Stage configuration is managed globally', { variant: 'info' })}
            onChangeInvoiceStatus={(status) => setViewPlan((prev) => ({ ...prev, invoice_status: { ...prev?.invoice_status, label: status } }))}
            enqueueSnackbar={enqueueSnackbar}
          />
        ) : null}

        {plannerCRU.modal ? (
          <PlannerCRU
            plan={plannerCRU.data}
            lookups={options}
            onClose={() => setPlannerCRU({ modal: false, data: null })}
            onSaved={() => { setPlannerCRU({ modal: false, data: null }); fetchPlans(); }}
            enqueueSnackbar={enqueueSnackbar}
          />
        ) : null}

        {confirmAction ? (
          <ConfirmActionDialog
            title={confirmAction.type === 'launch' ? 'Launch Project' : confirmAction.type === 'close' ? 'Close Project' : 'Cancel Project'}
            description={confirmAction.type === 'launch' ? 'Review data migration before confirming' : confirmAction.type === 'close' ? 'Both report statuses must be Closed and client acceptance recorded' : 'This moves the plan to the Cancelled state and archives it'}
            content={confirmAction.type === 'launch'
              ? `Launching "${confirmAction.plan?.name}" migrates its data and locks the setup fields.`
              : confirmAction.type === 'close'
                ? `Are you sure you want to close "${confirmAction.plan?.name}"?`
                : `Are you sure you want to cancel "${confirmAction.plan?.name}"?`}
            confirmText={confirmAction.type === 'launch' ? 'Confirm Launch' : confirmAction.type === 'close' ? 'Confirm Close' : 'Confirm Cancellation'}
            confirmColor={confirmAction.type === 'cancel' ? 'secondary' : 'primary'}
            loading={actionLoading}
            onClose={() => setConfirmAction(null)}
            onConfirm={handleConfirmAction}
          />
        ) : null}

        {stageChangeTarget ? (
          <ChangePlanStage
            plan={stageChangeTarget}
            canOverride={permissions.stages}
            onClose={() => setStageChangeTarget(null)}
            onSaved={() => { setStageChangeTarget(null); fetchPlans(); }}
            enqueueSnackbar={enqueueSnackbar}
          />
        ) : null}

        {deleteTarget ? (
          <DeletePopUp
            modal
            title="Delete Plan"
            content={<h4>Are you sure you want to delete "{deleteTarget?.name}"?</h4>}
            toggleModalFn={() => setDeleteTarget(null)}
            deleteRecordFn={handleConfirmDelete}
            circularloader={deleting}
          />
        ) : null}

        {!initialLoading && (loading || actionLoading) ? <PageLoader /> : null}
      </section>
    </div>
  );
};

export default ProgramPlanner;
